import Accordion from "@/common/components/Accordion";
import { ImageCarousel } from "@/common/components/ImageCarousel";
import { ImageWithPlaceholder } from "@/common/components/ImageWithPlaceholder";
import ResumeItem from "@/common/components/ResumeItem";
import Skeleton from "@/common/components/Skeleton";
import { formatDateTime } from "@/common/utils/formatters";
import { CameraOffIcon } from "lucide-react";
import { EmptyDataMessage } from "@/common/components/EmptyDataMessage";
import { useMaintenanceWorkflowSolicitations } from "@/features/privateCompanies/hooks/maintenance/workflows/useMaintenanceWorkflowSolicitations";
import { useParams } from "react-router-dom";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import { twMerge } from "tailwind-merge";
import { Pagination } from "@/common/components/Table/pagination";
import { useState } from "react";
import { MaintenanceSolicitationData } from "@/common/types/maintenance/maintenanceSolicitation";
import { solicitationKindTranslation } from "@/common/constants/maintenance/solicitationKind.constant";

const MaintenanceWorkflowSolicitations = () => {
  const { id } = useParams();

  const [page, setPage] = useState<number | undefined>(1);

  const {
    data: workflowSolicitations,
    meta: workflowSolicitationsMeta,
    isPending: workflowSolicitationsIsPending,
    error: workflowSolicitationsError = {} as any,
    refetch: refetchWorkflowSolicitations,
  } = useMaintenanceWorkflowSolicitations<MaintenanceSolicitationData[]>({
    workflowId: id,
    filters: {
      page,
    },
  });

  return (
    <ErrorMessageWrapper
      statusCode={workflowSolicitationsError?.status}
      refetchFunction={refetchWorkflowSolicitations}
    >
      <div className="flex-1">
        <div className="flex flex-col gap-2">
          {workflowSolicitationsIsPending && new Array(7).fill(null).map((_, i) => (
            <div
              key={`solicitation-loading-${i}`}
              className="min-h-[68px] flex items-center gap-3 px-4 bg-white-100 rounded-sm"
            >
              <Skeleton className="size-6 rounded-lg bg-white-100" />

              <div className="flex flex-col gap-0.5">
                <Skeleton className="h-5 w-20 bg-white-100" />

                <Skeleton className="h-4 w-16 bg-white-100" />
              </div>
            </div>
          ))}

          {!workflowSolicitationsIsPending && Number(workflowSolicitationsMeta.total) === 0 && (
            <EmptyDataMessage
              message="Não há solicitações para este equipamento"
            />
          )}

          {!workflowSolicitationsIsPending && workflowSolicitations?.map((solicitation, index) => (
            <Accordion
              key={solicitation.id}
              className="p-0"
              triggerClassName="px-4 py-4"
              bodyClassName="px-4 pb-4"
              defaultOpen={!index}
              head={(
                <div className="flex items-center">
                  <div className="flex flex-col items-start">
                    <p className="text-xs text-white-300">
                      {solicitationKindTranslation[solicitation.kind] || solicitation.kind}

                      &nbsp;

                      •

                      &nbsp;

                      {solicitation.objectives?.length === 1
                        ? `1 Objeto para análise`
                        : `${solicitation.objectives?.length} Objetos para análise`}
                    </p>
                  </div>
                </div>
              )}
              body={(
                <div className="flex gap-3">
                  <div className="flex flex-col gap-2 w-48">
                    <ImageCarousel
                      files={solicitation?.photos?.map((photo, i) => ({ id: `${solicitation}_${i}`, file: photo?.url })) || []}
                      className="size-48 border-none"
                      showRemoveButton={false}
                      Placeholder={(
                        <ImageWithPlaceholder
                          placeholderIcon={CameraOffIcon}
                          className="size-48 rounded-lg bg-white-800"
                          iconClassName="size-36"
                        />
                      )}
                      showExpandButton
                      showNavigationButtons
                    />

                    <ResumeItem
                      label="Autor"
                      value={solicitation?.creator?.full_name || '--'}
                      labelClassName="text-xs"
                      valueClassName="text-sm"
                    />

                    <ResumeItem
                      label="Data e hora da abertura"
                      value={formatDateTime(new Date(solicitation.created_at))}
                      labelClassName="text-xs"
                      valueClassName="text-sm"
                    />

                    <ResumeItem
                      label="Descrição"
                      value={solicitation.observations || '--'}
                      labelClassName="text-xs"
                      valueClassName="text-sm"
                    />
                  </div>

                  <div className="flex-1">
                    {Number(solicitation.objectives?.length) > 0 ? (
                      <div className="flex flex-col gap-2 max-h-[380px] overflow-auto">
                        {solicitation.objectives?.map((objective) => (
                          <div key={objective.id}>
                            <div className="bg-white-100 p-4 rounded-lg flex items-center gap-2">
                              <div className="flex items-center gap-2">
                                <div className="bg-white-100 p-2 rounded-md">
                                  <p className="text-xs font-bold">
                                    {objective.system.display_name}
                                  </p>
                                </div>

                                <div className="flex flex-col items-start">
                                  <p className="font-bold">
                                    {objective.action.display_name}

                                    &nbsp;

                                    <span className="font-normal">
                                      em
                                    </span>

                                    &nbsp;

                                    {objective.part.display_name}
                                  </p>

                                  <p className="text-xs">
                                    {objective.action.description}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <EmptyDataMessage
                        className="h-full py-0"
                        imageHeight={180}
                        message="Essa solicitação não possui objetos de manutenção"
                      />
                    )}
                  </div>
                </div>
              )}
            />
          ))}
        </div>

        {!workflowSolicitationsIsPending ? (
          <div
            className={twMerge(
              'w-full px-4 py-2 bg-white-200 rounded-b-lg flex justify-between items-center',
              typeof workflowSolicitationsMeta.total === 'undefined' && 'justify-end',
            )}
          >
            {typeof workflowSolicitationsMeta.total !== 'undefined' ? (
              <p className="text-xs italic font-light">
                Mostrando {workflowSolicitations?.length} resultados de {workflowSolicitationsMeta.total}
              </p>
            ) : null}
            {setPage && (
              <Pagination
                currentPage={page || 1}
                totalPages={workflowSolicitationsMeta.total_pages}
                setPage={setPage}
              />
            )}
          </div>
        ) : (
          <div className="h-8 w-full px-4 bg-white-200 rounded-b-lg flex justify-between items-center">
            <Skeleton className="h-4 w-40" />
          </div>
        )}
      </div>
    </ErrorMessageWrapper>
  );
}

export default MaintenanceWorkflowSolicitations;
